import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { QrCode, Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";

const MAX_QR_BYTES = 2 * 1024 * 1024;
const MAX_LOGO_BYTES = 1 * 1024 * 1024;
const ALLOWED_QR_MIMES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const ALLOWED_LOGO_MIMES = [...ALLOWED_QR_MIMES, "image/svg+xml"];

/**
 * Modal used for both CREATE (initial === null) and EDIT (initial passed in).
 * Fields: Name, Link, QR Code (image upload), Instructions, Enabled toggle.
 */
export default function PaymentSystemModal({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    account_name: "",
    account_number: "",
    link: "",
    qr_code_data_url: "",
    logo_data_url: "",
    instructions: "",
    enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const logoRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name || "",
        account_name: initial?.account_name || "",
        account_number: initial?.account_number || "",
        link: initial?.link || "",
        qr_code_data_url: initial?.qr_code_data_url || "",
        logo_data_url: initial?.logo_data_url || "",
        instructions: initial?.instructions || "",
        enabled: initial?.enabled ?? true,
      });
    }
  }, [open, initial]);

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_QR_MIMES.includes(file.type)) {
      toast.error("QR code must be a PNG, JPG, or WEBP image");
      return;
    }
    if (file.size > MAX_QR_BYTES) {
      toast.error("QR code image must be smaller than 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, qr_code_data_url: String(reader.result || "") }));
    reader.readAsDataURL(file);
  };

  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_LOGO_MIMES.includes(file.type)) {
      toast.error("Logo must be a PNG, JPG, WEBP, or SVG image");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo must be smaller than 1 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_data_url: String(reader.result || "") }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Payment system name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        account_name: form.account_name.trim(),
        account_number: form.account_number.trim(),
        link: form.link.trim(),
        qr_code_data_url: form.qr_code_data_url,
        logo_data_url: form.logo_data_url,
        instructions: form.instructions.trim(),
        enabled: !!form.enabled,
      };
      const r = initial?.id
        ? await api.put(`/settings/payment-systems/${initial.id}`, payload)
        : await api.post("/settings/payment-systems", payload);
      toast.success(initial?.id ? "Payment system updated" : "Payment system added");
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save payment system");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="payment-system-modal">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit Payment System" : "Add Payment System"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Payment System Name <span className="text-rose-500">*</span></Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. GCash, Maya, PayMongo, Bank Transfer"
              maxLength={80}
              data-testid="ps-name-input"
            />
          </div>

          <div>
            <Label>Logo <span className="text-slate-400 text-xs ml-1">optional</span></Label>
            <div className="flex items-start gap-3 mt-1">
              <div className="w-20 h-20 rounded-md border-2 border-dashed border-slate-300 dark:border-slate-700 grid place-items-center bg-slate-50 dark:bg-slate-800/40 overflow-hidden">
                {form.logo_data_url ? (
                  <img src={form.logo_data_url} alt="Logo preview" className="w-full h-full object-contain" />
                ) : (
                  <ImageIcon className="w-7 h-7 text-slate-400" strokeWidth={1.5} />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={logoRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={pickLogo}
                  data-testid="ps-logo-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => logoRef.current?.click()}
                  data-testid="ps-logo-upload-btn"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  {form.logo_data_url ? "Replace Logo" : "Upload Logo"}
                </Button>
                {form.logo_data_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-rose-600 hover:text-rose-700"
                    onClick={() => setForm({ ...form, logo_data_url: "" })}
                    data-testid="ps-logo-remove-btn"
                  >
                    <X className="w-3.5 h-3.5 mr-1.5" /> Remove
                  </Button>
                )}
                <p className="text-[11px] text-slate-500">PNG, JPG, WEBP, or SVG · Max 1 MB</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Account Name <span className="text-slate-400 text-xs ml-1">optional</span></Label>
              <Input
                value={form.account_name}
                onChange={(e) => setForm({ ...form, account_name: e.target.value })}
                placeholder="e.g. Juan Dela Cruz"
                maxLength={120}
                data-testid="ps-account-name-input"
              />
            </div>
            <div>
              <Label>Account Number <span className="text-slate-400 text-xs ml-1">optional</span></Label>
              <Input
                value={form.account_number}
                onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                placeholder="e.g. 0917-123-4567"
                maxLength={80}
                data-testid="ps-account-number-input"
              />
            </div>
          </div>

          <div>
            <Label>Link <span className="text-slate-400 text-xs ml-1">optional</span></Label>
            <Input
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              placeholder="https://…"
              maxLength={2048}
              data-testid="ps-link-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">Must start with http:// or https:// if provided.</p>
          </div>

          <div>
            <Label>QR Code <span className="text-slate-400 text-xs ml-1">optional</span></Label>
            <div className="flex items-start gap-3 mt-1">
              <div className="w-24 h-24 rounded-md border-2 border-dashed border-slate-300 dark:border-slate-700 grid place-items-center bg-slate-50 dark:bg-slate-800/40 overflow-hidden">
                {form.qr_code_data_url ? (
                  <img src={form.qr_code_data_url} alt="QR preview" className="w-full h-full object-contain" />
                ) : (
                  <QrCode className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  className="hidden"
                  onChange={pickFile}
                  data-testid="ps-qr-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  data-testid="ps-qr-upload-btn"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  {form.qr_code_data_url ? "Replace QR Code" : "Upload QR Code"}
                </Button>
                {form.qr_code_data_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-rose-600 hover:text-rose-700"
                    onClick={() => setForm({ ...form, qr_code_data_url: "" })}
                    data-testid="ps-qr-remove-btn"
                  >
                    <X className="w-3.5 h-3.5 mr-1.5" /> Remove
                  </Button>
                )}
                <p className="text-[11px] text-slate-500">PNG, JPG, or WEBP · Max 2 MB</p>
              </div>
            </div>
          </div>

          <div>
            <Label>Payment Instructions <span className="text-slate-400 text-xs ml-1">optional</span></Label>
            <Textarea
              rows={4}
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              placeholder="Explain how the student should pay using this method."
              maxLength={4000}
              data-testid="ps-instructions-input"
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/40 rounded-md border border-slate-200 dark:border-slate-700">
            <div>
              <Label className="text-sm font-semibold">Enabled</Label>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {form.enabled ? "Students will see this method." : "Hidden from students."}
              </p>
            </div>
            <Switch
              checked={!!form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
              data-testid="ps-enabled-toggle"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="ps-cancel-btn">
            Cancel
          </Button>
          <Button
            className="bg-[#1E3A8A] hover:bg-[#1E40AF]"
            onClick={save}
            disabled={saving}
            data-testid="ps-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {initial?.id ? "Save Changes" : "Add Payment System"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
